/** `Portal Version Three` (PVT) configuration levels. */
export const PvtLevels = {
  /** No override allowed; Account/app state decides whether V3 is enabled. */
  Off: 0,
  /** V3 force-enabled for all accounts, but the session may opt out via `?__pvt_off`. */
  Default: 1,
  /** V3 force-enabled for everyone; opt-out ignored. */
  Forced: 2,
} as const;

export type PvtLevel = (typeof PvtLevels)[keyof typeof PvtLevels];

/** Presence-based: key set means "this session opted out of forced V3". */
export const PVT_DISABLED_SESSION_KEY = 'pvt-disabled';
const PVT_OFF_PARAM = '__pvt_off';
const PVT_ON_PARAM = '__pvt_on';

/**
 * Initialize the pvt session opt-out from URL params. Call once on app startup.
 *
 * Only acts under {@link PvtLevels.Default} — under `Off` there is nothing to override,
 * and under `Forced` the session key must never be written, so a stale opt-out can't
 * take effect if the env is later downgraded to `Default`.
 *
 * - `?__pvt_off` → sets the opt-out
 * - `?__pvt_on` → clears the opt-out
 * - Neither param → leaves session state unchanged (sticky across navigations)
 */
export function initPvtOverride(
  pvtLevel: PvtLevel | null | undefined,
  location: { search: string },
  sessionStorage: { setItem(key: string, value: string): void; removeItem(key: string): void },
): void {
  if (pvtLevel !== PvtLevels.Default) return;

  try {
    if (!location?.search || !sessionStorage) return;

    const params = new URLSearchParams(location.search);
    if (params.has(PVT_OFF_PARAM)) {
      sessionStorage.setItem(PVT_DISABLED_SESSION_KEY, '1');
    } else if (params.has(PVT_ON_PARAM)) {
      sessionStorage.removeItem(PVT_DISABLED_SESSION_KEY);
    }
  } catch (_e) { /* SSR / restricted storage */ }
}

/**
 * Resolve the portal_v3 override for the given pvt level.
 *
 * - `Forced` → always `true`, without reading storage (opt-out cannot apply).
 * - `Default` → `true`, unless the session opted out.
 * - `Off` (or missing level) → `null`, no override applied.
 *
 * @returns `true` = force-enable, `false` = force-disable, `null` = no override
 */
export function resolvePvtOverride(
  pvtLevel: PvtLevel | null | undefined,
  sessionStorage: { getItem(key: string): string | null },
): boolean | null {
  if (pvtLevel == null) return null;

  if (pvtLevel >= PvtLevels.Forced) return true;

  if (pvtLevel === PvtLevels.Default) {
    try {
      if (!sessionStorage) return true;
      return sessionStorage.getItem(PVT_DISABLED_SESSION_KEY) !== '1';
    } catch (_e) {
      return true;
    }
  }

  return null;
}
